import React, { useState, useEffect } from 'react';
import ChatImproved from './ChatImproved';
import ChatMinimized from './ChatMinimized';
import ChatSettings from './ChatSettings';
import { UserSession } from '../../types';

interface ChatContainerProps {
  session: UserSession;
  flows?: any[];
  departamentos?: any[];
  colaboradores?: any[];
  onClose?: () => void;
  initialOpen?: boolean; // Nova prop para controlar estado inicial
  initialChannelId?: string; // Conversa a abrir direto (vem da notificação)
  initialChannelNome?: string; // Nome do remetente, caso a DM ainda não exista na API
}

const ChatContainer: React.FC<ChatContainerProps> = (props) => {
  const { initialOpen = false } = props;
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isMinimized, setIsMinimized] = useState(!initialOpen);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Sincronizar com prop externa sempre que mudar
  useEffect(() => {
    setIsOpen(initialOpen);
    setIsMinimized(!initialOpen);
  }, [initialOpen]);

  // 🔔 Avisar o ChatNotifier se o FlowChat está ou não na tela.
  // Com o chat aberto, quem cuida de badge/som são as abas daqui — o notificador
  // global fica quieto para não duplicar aviso.
  useEffect(() => {
    const aberto = isOpen && !isMinimized;
    window.dispatchEvent(new CustomEvent('flowchat:visivel', { detail: { aberto } }));
    return () => {
      window.dispatchEvent(new CustomEvent('flowchat:visivel', { detail: { aberto: false } }));
    };
  }, [isOpen, isMinimized]);

  // Solicitar permissão para notificações do navegador
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Função para mostrar notificação desktop (desabilitada temporariamente)
  // const showDesktopNotification = (title: string, body: string) => {
  //   const settings = JSON.parse(localStorage.getItem('chat_settings') || '{"enabled":true,"desktop":true}');
  //   if (!settings.enabled || !settings.desktop) return;
  //   if (!('Notification' in window)) return;
  //   if (Notification.permission !== 'granted') return;
  //   new Notification(`FlowChat - ${title}`, {
  //     body,
  //     icon: '/logo.png',
  //     badge: '/logo.png',
  //     tag: 'flowchat-message'
  //   });
  // };

  // Função para tocar som de notificação (desabilitada temporariamente)
  // const playNotificationSound = () => {
  //   const settings = JSON.parse(localStorage.getItem('chat_settings') || '{"enabled":true,"sound":true}');
  //   const volume = parseInt(localStorage.getItem('chat_sound_volume') || '70') / 100;
  //   if (!settings.enabled || !settings.sound) return;
  //   const audio = new Audio('/notification.mp3');
  //   audio.volume = volume;
  //   audio.play().catch(err => console.log('Erro ao tocar som:', err));
  // };

  // Simular recebimento de mensagem (será substituído por WebSocket)
  // const handleNewMessage = (message: any) => {
  //   if (isMinimized || !document.hasFocus()) {
  //     setUnreadCount(prev => prev + 1);
  //     showDesktopNotification(
  //       `Nova mensagem de ${message.userName}`,
  //       message.content
  //     );
  //     playNotificationSound();
  //   }
  // };

  // Limpar contador ao expandir
  useEffect(() => {
    if (!isMinimized && isOpen) {
      setUnreadCount(0);
    }
  }, [isMinimized, isOpen]);

  // Não renderizar se foi fechado completamente
  if (!isOpen) {
    return null;
  }
  
  // Renderizar minimizado
  if (isMinimized) {
    return (
      <>
        <ChatMinimized
          unreadCount={unreadCount}
          onExpand={() => {
            setIsMinimized(false);
            setIsOpen(true);
          }}
          onClose={() => setIsOpen(false)} // Fechar completamente
          onSettings={() => setShowSettings(true)}
        />
        {showSettings && <ChatSettings onClose={() => setShowSettings(false)} />}
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col">
      {/* Header responsivo */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-3 sm:px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          {props.onClose && (
            <button
              onClick={() => {
                console.log('🔙 Botão voltar clicado');
                props.onClose?.();
              }}
              className="sm:hidden p-1.5 text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
              aria-label="Voltar"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h1 className="text-white font-bold text-base sm:text-lg flex items-center gap-2">
            ⚡ FlowChat
          </h1>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 sm:p-2 text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
            title="Configurações"
            aria-label="Configurações"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Minimizar"
            aria-label="Minimizar"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Chat Principal */}
      <div className="flex-1 overflow-hidden">
        <ChatImproved
          {...props}
          initialChannelId={props.initialChannelId}
          initialChannelNome={props.initialChannelNome}
        />
      </div>

      {/* Modal de Configurações */}
      {showSettings && <ChatSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
};

export default ChatContainer;
